import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Monogram, Wordmark } from "../components/brand/Logo";
import { LogoLockup } from "../components/brand/LogoLockup";
import { Button } from "../components/brand/Button";
import { CinematicCanvas, MonogramWatermark, Reveal, DrawRule, Eyebrow, CornerBrackets } from "../components/brand/Cinematic";
import { BrandFooter } from "../components/brand/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { n: "01", title: "Review",         body: "Our team reviews your application and stats privately." },
  { n: "02", title: "Discovery Call", body: "If you're a fit, we schedule a 30-min strategy call." },
  { n: "03", title: "Onboarding",     body: "Sign the agreement, build your roadmap with leadership." },
];

const BUCKETS = [
  "Prefer not to say",
  "Under $5K",
  "$5K — $15K",
  "$15K — $50K",
  "$50K — $150K",
  "$150K+",
];

function useApplicationRef() {
  const location = useLocation();
  const [params] = useSearchParams();
  const fromState = location.state?.ref;
  const fromSearch = params.get("ref");
  let fromSession = null;
  try { fromSession = sessionStorage.getItem("omb.last_application_ref"); }
  catch (err) { console.warn("Applied: sessionStorage read unavailable", err); }
  const ref = fromState || fromSearch || fromSession;
  useEffect(() => {
    if (ref) {
      try { sessionStorage.setItem("omb.last_application_ref", ref); }
      catch (err) { console.warn("Applied: sessionStorage write unavailable", err); }
    }
  }, [ref]);
  return ref;
}

function MicroSurvey({ refValue }) {
  const [bucket, setBucket] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!bucket) return;
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/surveys/monthly-revenue`, {
        answer_bucket: bucket,
        application_ref_optional: refValue || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send — please try again.");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <motion.p
        data-testid="survey-thanks"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
        className="font-display italic text-brand-text/75" style={{ fontSize: "clamp(20px, 2vw, 26px)" }}
      >
        Thank you.
      </motion.p>
    );
  }

  return (
    <form onSubmit={submit} data-testid="survey-form" className="mx-auto flex max-w-lg flex-col items-stretch gap-4">
      <div className="relative">
        <select
          data-testid="survey-bucket-select"
          value={bucket}
          onChange={(e) => setBucket(e.target.value)}
          className="block w-full appearance-none rounded-full border border-brand-text/15 bg-brand-bg/40 px-5 py-3.5 text-sm text-brand-text outline-none transition-all focus:border-brand-gold/60 focus:shadow-[0_0_0_3px_rgba(201,168,76,0.12)]"
        >
          <option value="" disabled>Choose a bucket…</option>
          {BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <span aria-hidden className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-brand-gold/60">▾</span>
      </div>
      {error && <p className="text-center text-xs text-brand-danger/85">{error}</p>}
      <Button data-testid="survey-submit" type="submit" variant="secondary" size="md" disabled={!bucket || busy}>
        {busy ? "Sending…" : "Send"}
      </Button>
    </form>
  );
}

export default function Applied() {
  const ref = useApplicationRef();
  const [stableRef] = useState(ref);
  const display = ref || stableRef;
  useEffect(() => { document.title = "Application Received · Omertàbella"; }, []);

  return (
    <main data-testid="applied-page" className="relative min-h-screen bg-brand-bg text-brand-text-warm">
      <CinematicCanvas />
      <MonogramWatermark className="right-[-22%] top-[15%]" />

      <header className="relative z-20 mx-auto flex max-w-3xl items-center justify-center px-6 pt-8 sm:px-10">
        <LogoLockup variant="header" as={Link} to="/" />
      </header>

      <section className="relative z-10 mx-auto max-w-3xl px-6 pt-16 text-center sm:px-10 sm:pt-24">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-brand-gold">Application received</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 data-testid="applied-headline"
              className="mt-10 font-display font-normal text-brand-text text-balance"
              style={{ fontSize: "clamp(48px, 7vw, 104px)", letterSpacing: "-0.04em", lineHeight: 1 }}>
            Welcome <em className="italic text-brand-gold">to the room.</em>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <DrawRule className="mx-auto mt-8" width="w-16" />
          <p className="mx-auto mt-10 max-w-xl text-base leading-relaxed text-brand-text/65 sm:text-lg">
            Your application is now under review by leadership at Omertàbella. We respond to every applicant within 48 hours, and all submissions are handled with full discretion.
          </p>
        </Reveal>
        {display && (
          <Reveal delay={0.35}>
            <p data-testid="applied-reference"
               className="mt-12 inline-flex items-center gap-3 rounded-full border border-brand-text/10 bg-brand-bg/40 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.32em] text-brand-text/55">
              Reference
              <span className="h-px w-4 bg-brand-text/20" />
              <span data-testid="applied-reference-id" className="text-brand-gold">{display}</span>
            </p>
          </Reveal>
        )}
      </section>

      {/* Timeline */}
      <section className="relative z-10 mx-auto mt-28 max-w-4xl px-6 sm:mt-36 sm:px-10">
        <Reveal>
          <div className="text-center"><Eyebrow index="—">What Happens Next</Eyebrow></div>
        </Reveal>
        <ol className="mt-14 space-y-6 sm:space-y-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <li data-testid={`applied-step-${s.n}`}
                  className="glass relative flex items-start gap-6 overflow-hidden rounded-3xl p-7 sm:gap-8 sm:p-10">
                <CornerBrackets size={16} opacity={0.3} />
                <div className="relative shrink-0">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-brand-gold/40 bg-brand-bg/40">
                    <span className="font-display text-xl text-brand-gold">{s.n}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <span className="absolute left-1/2 top-full block h-8 w-px -translate-x-1/2 bg-gradient-to-b from-brand-gold/40 to-transparent" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl text-brand-text sm:text-2xl" style={{ letterSpacing: "-0.02em" }}>{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-text/65 sm:text-base">{s.body}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* Micro-survey */}
      <section className="relative z-10 mx-auto mt-28 max-w-2xl px-6 text-center sm:mt-36 sm:px-10">
        <Reveal>
          <p className="font-display italic text-brand-text/70" style={{ fontSize: "clamp(16px, 1.6vw, 20px)" }}>
            One last thing — optional.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="mx-auto mt-6 max-w-xl font-display text-2xl text-brand-text sm:text-3xl" style={{ letterSpacing: "-0.02em", lineHeight: 1.15 }}>
            What's your current average monthly revenue with your existing management?
          </h3>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10"><MicroSurvey refValue={display} /></div>
        </Reveal>
      </section>

      {/* Footer CTAs */}
      <section className="relative z-10 mx-auto mt-28 max-w-3xl px-6 pb-24 text-center sm:px-10">
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Button as={Link} to="/" data-testid="applied-home-btn" variant="ghost">Return to home</Button>
          <Button as={Link} to="/login" data-testid="applied-signin-btn">Sign in</Button>
        </div>
      </section>
      <BrandFooter />
    </main>
  );
}
